import { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { createPortal } from 'react-dom';
import { get_artwork_thumbnail_url } from '../../utils/formatters';
import {
  Minus,
  Plus,
  Trash2,
  ArrowRight,
  ArrowLeft,
  X,
  ShoppingBag,
  MapPin,
  Loader2,
  ChevronDown,
  ImageIcon,
  Check,
  Info,
} from 'lucide-react';
import { GlassSurface } from './GlassSurface';
import { overlay_variants, modal_variants } from '../../utils/animations';
import { useShippingCost } from '../../hooks/useShippingCost';
import { useCities } from '../../hooks/useCities';

/**
 * CartPanel - "Studio Cart"
 *
 * Mobile: Layout vertical centrado
 * Desktop (lg+): Dos columnas (items izq | resumen+checkout der)
 *
 * @param {Object} props
 * @param {boolean} props.is_open - Estado de visibilidad
 * @param {Function} props.on_close - Callback al cerrar
 * @param {Array} props.items - Items del carrito
 * @param {Function} props.on_update_quantity - Callback al cambiar cantidad (item_id, new_quantity)
 * @param {Function} props.on_remove_item - Callback al eliminar item (item_id)
 * @param {Function} props.on_checkout - Callback al ir a checkout
 * @param {Function} props.on_continue_shopping - Callback al seguir comprando
 */
export function CartPanel({
  is_open = false,
  on_close,
  items = [],
  on_update_quantity,
  on_remove_item,
  on_checkout,
  on_continue_shopping,
}) {
  const navigate = useNavigate();

  // Auth state for shipping calculation
  const { user, is_authenticated } = useSelector(state => state.auth);
  const saved_address = user?.default_shipping_address || null;

  // Shipping hooks
  const {
    cost_data,
    loading: shipping_loading,
    error_state: shipping_error,
    calculateCost,
    clearCost,
  } = useShippingCost();

  // City selector state (when no saved address)
  const [selected_region, set_selected_region] = useState('');
  const [selected_city, set_selected_city] = useState('');
  const [selected_city_name, set_selected_city_name] = useState('');
  const [show_city_selector, set_show_city_selector] = useState(false);
  const {
    regions,
    cities,
    loading_regions,
    loading_cities,
    fetchCities,
  } = useCities();

  // Calcular totales
  const subtotal = items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const shipping_cost = cost_data?.cost || 0;
  const total = subtotal + shipping_cost;
  const item_count = items.reduce((sum, item) => sum + item.quantity, 0);

  // Formato de precio - CLP
  const format_price = (price) => {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
      minimumFractionDigits: 0,
    }).format(price);
  };

  // Manejo de ESC
  const handle_key_down = useCallback(
    (event) => {
      if (event.key === 'Escape' && on_close) {
        on_close();
      }
    },
    [on_close]
  );

  // Back gesture support — see MenuPanel.jsx for detailed explanation.
  // Key check prevents undoing navigations; deferred cleanup survives StrictMode.
  const history_key_ref = useRef(null);
  const cleanup_timer_ref = useRef(null);

  useEffect(() => {
    if (is_open) {
      if (cleanup_timer_ref.current) {
        clearTimeout(cleanup_timer_ref.current);
        cleanup_timer_ref.current = null;
      }

      document.addEventListener('keydown', handle_key_down);
      document.body.style.overflow = 'hidden';

      const state_key = `cart_${Date.now()}`;
      window.history.pushState({ panel: 'cart', key: state_key }, '');
      history_key_ref.current = state_key;

      const handle_popstate = () => {
        history_key_ref.current = null;
        on_close?.();
      };
      window.addEventListener('popstate', handle_popstate);

      return () => {
        document.removeEventListener('keydown', handle_key_down);
        document.body.style.overflow = '';
        window.removeEventListener('popstate', handle_popstate);
        const key_to_clean = history_key_ref.current;
        history_key_ref.current = null;
        if (key_to_clean && window.history.state?.key === key_to_clean) {
          cleanup_timer_ref.current = setTimeout(() => {
            cleanup_timer_ref.current = null;
            if (window.history.state?.key === key_to_clean) {
              window.history.back();
            }
          }, 0);
        }
      };
    }

    return () => {
      document.removeEventListener('keydown', handle_key_down);
      document.body.style.overflow = '';
    };
  }, [is_open, handle_key_down, on_close]);

  // Auto-calculate shipping when panel opens
  useEffect(() => {
    if (!is_open || items.length === 0) {
      clearCost();
      return;
    }

    const first_item = items[0];
    if (!first_item?.id) return;

    // If user has a saved address with county_code, auto-calculate
    if (saved_address?.county_code) {
      calculateCost({
        artwork_id: first_item.id,
        destination_county_code: saved_address.county_code,
      });
      return;
    }

    // If user selected a city manually, calculate
    if (selected_city) {
      calculateCost({
        artwork_id: first_item.id,
        destination_county_code: selected_city,
      });
    }
  }, [is_open, items, saved_address?.county_code, selected_city, calculateCost, clearCost]);

  // Region change handler
  const handle_region_change = (region_id) => {
    set_selected_region(region_id);
    set_selected_city('');
    set_selected_city_name('');
    clearCost();
    if (region_id) {
      fetchCities(region_id);
    }
  };

  // City change handler
  const handle_city_change = (county_code) => {
    set_selected_city(county_code);
    const city = cities.find(c => c.id === county_code);
    set_selected_city_name(city?.name || '');
    set_show_city_selector(false);
  };

  const handle_quantity_change = (item_id, delta) => {
    const item = items.find(i => i.id === item_id);
    if (item) {
      const new_quantity = Math.max(1, item.quantity + delta);
      on_update_quantity?.(item_id, new_quantity);
    }
  };

  // Get display city name
  const get_shipping_city_name = () => {
    if (saved_address?.city) return saved_address.city;
    if (selected_city_name) return selected_city_name;
    return null;
  };

  // Componente reutilizable para un item del carrito
  const CartItem = ({ item, variant = 'compact' }) => (
    <GlassSurface
      variant="light"
      shadow="none"
      className={`rounded-lg ${variant === 'expanded' ? 'p-5' : 'p-4'}`}
    >
      <div className={`flex ${variant === 'expanded' ? 'gap-4' : 'gap-3'}`}>
        {/* Imagen — clickeable para ir a la obra */}
        <button
          onClick={() => { on_close?.(); navigate(`/obra/${item.id}`); }}
          className={`flex-shrink-0 rounded-lg overflow-hidden bg-raios-secondary cursor-pointer ${variant === 'expanded' ? 'w-36 h-36' : 'w-24 h-24'}`}
          aria-label={`Ver ${item.title}`}
        >
          {item.image_url ? (
            <img
              src={get_artwork_thumbnail_url(item.image_url, variant === 'expanded' ? 288 : 192)}
              alt={item.title}
              className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <ImageIcon size={24} className="text-raios-text-support" />
            </div>
          )}
        </button>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <button
            onClick={() => { on_close?.(); navigate(`/obra/${item.id}`); }}
            className="text-left w-full"
          >
            <h4 className={`font-mono text-raios-text-high truncate hover:text-raios-primary transition-colors ${variant === 'expanded' ? 'text-lg' : 'text-sm'}`}>
              {item.title}
            </h4>
          </button>
          <p className={`text-raios-text-support truncate ${variant === 'expanded' ? 'text-sm' : 'text-xs'}`}>
            {item.artist_name}
          </p>
          <p className={`font-mono text-raios-primary mt-1 ${variant === 'expanded' ? 'text-lg' : 'text-sm'}`}>
            {format_price(item.price)}
          </p>

          {/* Controles de cantidad */}
          <div className={`flex items-center justify-between ${variant === 'expanded' ? 'mt-4' : 'mt-2'}`}>
            <div className="flex items-center gap-2">
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={() => handle_quantity_change(item.id, -1)}
                disabled={item.quantity <= 1}
                className={`rounded-md bg-white/10 flex items-center justify-center text-raios-text-support hover:text-raios-text-high disabled:opacity-30 disabled:cursor-not-allowed ${variant === 'expanded' ? 'w-9 h-9' : 'w-8 h-8'}`}
              >
                <Minus size={variant === 'expanded' ? 16 : 14} strokeWidth={2} />
              </motion.button>
              <span className={`font-mono text-raios-text-high text-center ${variant === 'expanded' ? 'text-base w-8' : 'text-sm w-6'}`}>
                {item.quantity}
              </span>
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={() => handle_quantity_change(item.id, 1)}
                className={`rounded-md bg-white/10 flex items-center justify-center text-raios-text-support hover:text-raios-text-high ${variant === 'expanded' ? 'w-9 h-9' : 'w-8 h-8'}`}
              >
                <Plus size={variant === 'expanded' ? 16 : 14} strokeWidth={2} />
              </motion.button>
            </div>

            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={() => on_remove_item?.(item.id)}
              className="p-2 rounded-md text-raios-text-support hover:text-red-400 hover:bg-red-400/10 transition-colors"
            >
              <Trash2 size={variant === 'expanded' ? 18 : 16} strokeWidth={2} />
            </motion.button>
          </div>
        </div>
      </div>
    </GlassSurface>
  );

  // Componente vacío
  const EmptyCart = ({ variant = 'mobile' }) => (
    <div className={`flex flex-col items-center justify-center text-center ${variant === 'desktop' ? 'py-16' : 'h-full py-12'}`}>
      <div className="w-20 h-20 rounded-full bg-raios-primary/10 flex items-center justify-center mb-4">
        <ShoppingBag size={32} className="text-raios-primary/50" strokeWidth={2} />
      </div>
      <p className="text-raios-text-support mb-4 text-lg">
        Tu carrito está vacío
      </p>
      <motion.button
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        onClick={() => {
          on_continue_shopping?.();
          on_close?.();
        }}
        className="text-raios-primary hover:text-raios-tertiary transition-colors"
      >
        Explorar obras →
      </motion.button>
    </div>
  );

  // Shipping section sub-component
  const ShippingSection = () => {
    const city_name = get_shipping_city_name();
    const has_city = !!(saved_address?.county_code || selected_city);

    // Not authenticated
    if (!is_authenticated) {
      return (
        <div className="flex justify-between text-sm">
          <span className="text-raios-text-support">Envío</span>
          <span className="text-raios-text-support text-xs text-right">
            Inicia sesión para calcular
          </span>
        </div>
      );
    }

    // Authenticated with city → show cost states
    if (has_city && !show_city_selector) {
      return (
        <div className="space-y-1">
          <div className="flex justify-between text-sm">
            <span className="text-raios-text-support">Envío</span>
            <div className="text-right">
              {shipping_loading ? (
                <span className="flex items-center gap-1.5 text-raios-text-support text-xs">
                  <Loader2 size={12} className="animate-spin" />
                  Calculando...
                </span>
              ) : shipping_error?.has_error ? (
                <span className="text-red-400 text-xs">No disponible</span>
              ) : cost_data ? (
                <span className="font-mono text-raios-text-high">
                  {format_price(cost_data.cost)}
                </span>
              ) : null}
            </div>
          </div>

          {/* City name + estimated days + change button */}
          {cost_data && !shipping_loading && (
            <div className="flex items-center justify-between">
              <span className="text-xs text-raios-text-support flex items-center gap-1">
                <MapPin size={10} />
                {city_name}
                {cost_data.estimated_days && (
                  <span> · ~{cost_data.estimated_days} días</span>
                )}
              </span>
              <button
                onClick={() => set_show_city_selector(true)}
                className="text-xs text-raios-primary hover:text-raios-tertiary transition-colors"
              >
                Cambiar
              </button>
            </div>
          )}

          {/* Show change button even on error */}
          {shipping_error?.has_error && !shipping_loading && (
            <div className="flex justify-end">
              <button
                onClick={() => set_show_city_selector(true)}
                className="text-xs text-raios-primary hover:text-raios-tertiary transition-colors"
              >
                Cambiar ciudad
              </button>
            </div>
          )}
        </div>
      );
    }

    // Authenticated without city (or changing city) → compact selector
    return (
      <div className="space-y-2">
        <div className="flex justify-between text-sm">
          <span className="text-raios-text-support">Envío</span>
          <span className="text-raios-text-support text-xs">Selecciona tu ciudad</span>
        </div>

        {/* Region selector */}
        <div className="relative">
          <select
            value={selected_region}
            onChange={(e) => handle_region_change(e.target.value)}
            disabled={loading_regions}
            className="w-full text-xs bg-white/5 border border-raios-text-support/20 rounded-md px-2 py-1.5 text-raios-text-high appearance-none cursor-pointer focus:outline-none focus:border-raios-primary/50"
          >
            <option value="">
              {loading_regions ? 'Cargando regiones...' : 'Región'}
            </option>
            {regions.map((region) => (
              <option key={region.id} value={region.id}>
                {region.name}
              </option>
            ))}
          </select>
          <ChevronDown size={12} className="absolute right-2 top-1/2 -translate-y-1/2 text-raios-text-support pointer-events-none" />
        </div>

        {/* City selector */}
        {selected_region && (
          <div className="relative">
            <select
              value={selected_city}
              onChange={(e) => handle_city_change(e.target.value)}
              disabled={loading_cities}
              className="w-full text-xs bg-white/5 border border-raios-text-support/20 rounded-md px-2 py-1.5 text-raios-text-high appearance-none cursor-pointer focus:outline-none focus:border-raios-primary/50"
            >
              <option value="">
                {loading_cities ? 'Cargando ciudades...' : 'Ciudad'}
              </option>
              {cities.map((city) => (
                <option key={city.id} value={city.id}>
                  {city.name}
                </option>
              ))}
            </select>
            <ChevronDown size={12} className="absolute right-2 top-1/2 -translate-y-1/2 text-raios-text-support pointer-events-none" />
          </div>
        )}

        {/* Cancel button if changing from existing city */}
        {(saved_address?.county_code || selected_city) && show_city_selector && (
          <button
            onClick={() => set_show_city_selector(false)}
            className="text-xs text-raios-text-support hover:text-raios-text-high transition-colors"
          >
            Cancelar
          </button>
        )}
      </div>
    );
  };

  // Componente de resumen
  const CheckoutSummary = ({ variant = 'mobile' }) => (
    <div className={variant === 'desktop' ? '' : 'bg-raios-secondary/50'}>
      {/* Resumen */}
      <div className="space-y-3 mb-6">
        <div className="flex justify-between text-sm">
          <span className="text-raios-text-support">Subtotal ({item_count} {item_count === 1 ? 'obra' : 'obras'})</span>
          <span className="text-raios-text-high">{format_price(subtotal)}</span>
        </div>

        {/* Shipping section */}
        <ShippingSection />

        {/* Single-artist cart note */}
        {items.length > 0 && (
          <div className="flex items-start gap-2 p-2 bg-white/5 rounded-lg">
            <Info size={14} className="text-raios-text-support flex-shrink-0 mt-0.5" />
            <p className="text-[11px] text-raios-text-support leading-tight">
              Cada compra incluye obras de un mismo artista para un despacho coordinado.
            </p>
          </div>
        )}

        <div className="border-t border-raios-text-support/10 pt-3">
          <div className="flex justify-between items-baseline">
            <span className="font-mono text-raios-text-high">Total</span>
            <span className="font-mono text-2xl text-raios-text-high">
              {format_price(total)}
            </span>
          </div>
          {cost_data?.cost > 0 && (
            <p className="text-[10px] text-raios-text-support text-right mt-1">
              * Costo de envío estimado
            </p>
          )}
        </div>
      </div>

      {/* CTA Checkout */}
      <motion.button
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        onClick={on_checkout}
        className="w-full flex items-center justify-center gap-2 p-4 bg-raios-primary text-white rounded-lg font-mono text-sm hover:bg-raios-primary/90 transition-colors min-h-[48px]"
      >
        Continuar compra
        <ArrowRight size={18} strokeWidth={2} />
      </motion.button>

      {/* Link seguir explorando */}
      <motion.button
        whileHover={{ x: -4 }}
        onClick={() => {
          on_continue_shopping?.();
          on_close?.();
        }}
        className="w-full flex items-center justify-center gap-2 mt-3 p-2 text-raios-text-support hover:text-raios-text-high transition-colors text-sm min-h-[44px]"
      >
        <ArrowLeft size={16} strokeWidth={2} />
        Seguir explorando
      </motion.button>
    </div>
  );

  const modal_content = (
    <AnimatePresence>
      {is_open && (
        <div className="fixed inset-0 z-40 flex items-center justify-center p-3 md:p-4 lg:p-8">
          {/* Overlay */}
          <motion.div
            variants={overlay_variants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="absolute inset-0 bg-raios-secondary/85 backdrop-blur-md"
            onClick={on_close}
          />

          {/* Panel - Responsive */}
          <GlassSurface
            as_motion
            variants={modal_variants}
            initial="hidden"
            animate="visible"
            exit="exit"
            variant="surface"
            shadow="float"
            className="
              relative w-full rounded-xl overflow-hidden
              max-w-[95vw] md:max-w-lg lg:max-w-4xl xl:max-w-5xl
              max-h-[90vh] lg:max-h-[80vh]
            "
          >
            {/* Botón cerrar - Absoluto */}
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              onClick={on_close}
              className="
                absolute top-3 right-3 lg:top-5 lg:right-5 z-10
                p-2 rounded-lg
                text-raios-text-support hover:text-raios-text-high
                hover:bg-white/10 transition-colors
                min-w-[44px] min-h-[44px] flex items-center justify-center
              "
              aria-label="Cerrar"
            >
              <X size={20} strokeWidth={2} />
            </motion.button>

            {/* ============ MOBILE/TABLET LAYOUT ============ */}
            <div className="lg:hidden flex flex-col h-full max-h-[90vh]">
              {/* Header */}
              <div className="p-4 md:p-5 border-b border-raios-text-support/10">
                <h2 className="font-mono text-xl text-raios-text-high pr-10">
                  Tu Selección
                </h2>
                <p className="text-sm text-raios-text-support mt-1">
                  {item_count} {item_count === 1 ? 'obra' : 'obras'} · {format_price(subtotal)}
                </p>
              </div>

              {/* Items */}
              <div className="flex-1 overflow-y-auto p-4 md:p-5">
                {items.length === 0 ? (
                  <EmptyCart variant="mobile" />
                ) : (
                  <div className="space-y-3">
                    {items.map((item) => (
                      <CartItem key={item.id} item={item} variant="compact" />
                    ))}
                  </div>
                )}
              </div>

              {/* Resumen y CTA */}
              {items.length > 0 && (
                <div className="p-4 md:p-5 border-t border-raios-text-support/10 bg-raios-secondary/50">
                  <CheckoutSummary variant="mobile" />
                </div>
              )}
            </div>

            {/* ============ DESKTOP LAYOUT (lg+) - Dos columnas ============ */}
            <div className="hidden lg:flex h-full max-h-[80vh]">
              {/* Columna izquierda - Items */}
              <div className="flex-1 min-w-0 flex flex-col border-r border-raios-text-support/10">
                {/* Header */}
                <div className="p-5 xl:p-6 border-b border-raios-text-support/10">
                  <h2 className="font-mono text-2xl text-raios-text-high">
                    Tu Selección
                  </h2>
                  <p className="text-sm text-raios-text-support mt-1">
                    {item_count} {item_count === 1 ? 'obra' : 'obras'} en tu carrito
                  </p>
                </div>

                {/* Lista de items scrolleable */}
                <div className="flex-1 overflow-y-auto p-5 xl:p-6">
                  {items.length === 0 ? (
                    <EmptyCart variant="desktop" />
                  ) : (
                    <div className="space-y-4">
                      {items.map((item) => (
                        <CartItem key={item.id} item={item} variant="expanded" />
                      ))}
                    </div>
                  )}
                </div>

                {/* Link seguir explorando - solo si hay items */}
                {items.length > 0 && (
                  <div className="p-5 xl:p-6 border-t border-raios-text-support/10">
                    <motion.button
                      whileHover={{ x: -4 }}
                      onClick={() => {
                        on_continue_shopping?.();
                        on_close?.();
                      }}
                      className="flex items-center gap-2 text-raios-text-support hover:text-raios-text-high transition-colors text-sm"
                    >
                      <ArrowLeft size={16} strokeWidth={2} />
                      Seguir explorando
                    </motion.button>
                  </div>
                )}
              </div>

              {/* Columna derecha - Resumen (sticky) */}
              <div className="w-80 xl:w-96 flex flex-col bg-raios-secondary/30">
                <div className="p-5 xl:p-6 flex-1 flex flex-col">
                  <h3 className="font-mono text-lg text-raios-text-high mb-6">
                    Resumen del pedido
                  </h3>

                  {items.length > 0 ? (
                    <div className="flex-1 flex flex-col justify-between">
                      <CheckoutSummary variant="desktop" />
                    </div>
                  ) : (
                    <div className="flex-1 flex items-center justify-center">
                      <p className="text-raios-text-support text-sm text-center">
                        Agrega obras para ver el resumen
                      </p>
                    </div>
                  )}
                </div>

                {/* Badges de confianza */}
                {items.length > 0 && (
                  <div className="p-5 xl:p-6 border-t border-raios-text-support/10">
                    <div className="flex flex-wrap gap-3 text-xs text-raios-text-support">
                      <span className="flex items-center gap-1">
                        <Check size={12} className="text-green-400" /> Pago seguro
                      </span>
                      <span className="flex items-center gap-1">
                        <Check size={12} className="text-green-400" />
                        {cost_data?.cost > 0 ? 'Envío calculado' : 'Envío a todo Chile'}
                      </span>
                      <span className="flex items-center gap-1">
                        <Check size={12} className="text-green-400" /> Garantía de autenticidad
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </GlassSurface>
        </div>
      )}
    </AnimatePresence>
  );

  // Renderizar en portal (SSR-safe)
  if (typeof window === 'undefined') return null;
  return createPortal(modal_content, document.body);
}

export default CartPanel;
